import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { useSnackbar } from "notistack";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import { IoClose } from "react-icons/io5";
import MaInput from "../../../../Components/UI/ma-input";
import MaButton from "../../../../Components/UI/ma-button";
import { ADD_ACCOUNT } from "../../../../Utils/Mutations";

const INITIAL_FORM = {
  bankName: "",
  accountName: "",
  accountNumber: "",
  confirmAccountNumber: "",
  routingNumber: "",
};

export function AddAccountModal({ open, onClose, onSuccess }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const { enqueueSnackbar } = useSnackbar();

  const [addAccount, { loading }] = useMutation(ADD_ACCOUNT);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const resetAndClose = () => {
    setForm(INITIAL_FORM);
    onClose?.();
  };

  const accountsMatch =
    form.accountNumber.length > 0 &&
    form.accountNumber === form.confirmAccountNumber;

  const isValid =
    form.bankName.trim() &&
    form.accountName.trim() &&
    form.accountNumber.trim() &&
    form.routingNumber.trim() &&
    accountsMatch;

  const handleSubmit = async () => {
    if (!isValid || loading) return;
    try {
      const res = await addAccount({
        variables: {
          bankName: form.bankName.trim(),
          accountName: form.accountName.trim(),
          accountNumber: form.accountNumber.trim(),
          routingNumber: form.routingNumber.trim(),
        },
      });
      enqueueSnackbar("Bank account added successfully!", { variant: "success" });
      onSuccess?.(res?.data?.addNewBank ?? null);
      resetAndClose();
    } catch (err) {
      enqueueSnackbar(err?.message ?? "Couldn’t add account.", { variant: "error" });
    }
  };

  const confirmHasError =
    form.confirmAccountNumber.length > 0 &&
    form.accountNumber !== form.confirmAccountNumber;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          maxWidth: 480,
          width: "100%",
        },
      }}
    >
      <DialogTitle className="flex items-center justify-between pr-2">
        <span className="text-[17px] font-semibold text-[#18181B]">
          Add Account
        </span>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          disabled={loading}
        >
          <IoClose className="text-xl text-[#6B7280]" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="space-y-4 pt-2">
        <MaInput
          label="Bank Name"
          fullWidth
          value={form.bankName}
          onChange={update("bankName")}
          placeholder="Bank name"
          disabled={loading}
          borderColor="#E5E7EB"
        />
        <MaInput
          label="Account Number"
          fullWidth
          value={form.accountNumber}
          onChange={update("accountNumber")}
          placeholder="Account number"
          inputMode="numeric"
          disabled={loading}
          borderColor="#E5E7EB"
        />
        <MaInput
          label="Confirm Account Number"
          fullWidth
          value={form.confirmAccountNumber}
          onChange={update("confirmAccountNumber")}
          placeholder="Confirm account number"
          inputMode="numeric"
          disabled={loading}
          borderColor={confirmHasError ? "#DC2626" : "#E5E7EB"}
          error={confirmHasError}
          helperText={confirmHasError ? "Account numbers do not match" : ""}
        />
        <MaInput
          label="Account Name"
          fullWidth
          value={form.accountName}
          onChange={update("accountName")}
          placeholder="Account holder name"
          disabled={loading}
          borderColor="#E5E7EB"
        />
        <MaInput
          label="Routing Number"
          fullWidth
          value={form.routingNumber}
          onChange={update("routingNumber")}
          placeholder="Routing number"
          inputMode="numeric"
          disabled={loading}
          borderColor="#E5E7EB"
        />
      </DialogContent>

      <DialogActions sx={{mb:2, px:3}} className="gap-2 px-4 pb-4 pt-0">
        <MaButton
          title="Cancel"
          onClick={onClose}
          disabled={loading}
          sx={{
            bgcolor: "transparent",
            color: "#6B7280",
            border: "none",
            boxShadow: "none",
            minWidth: "100px",
            border:'1px solid #EAEAEA80',
            "&:hover": { bgcolor: "#F9FAFB" },
          }}
        />
        <MaButton
          variant="primary"
          title={loading ? "Saving…" : "Save Account"}
          disabled={!isValid || loading}
          loading={loading}
          onClick={handleSubmit}
          sx={{
            bgcolor: "#2E1834",
            minHeight: "44px",
            px: 3,
            borderRadius: "12px",
          }}
        />
      </DialogActions>
    </Dialog>
  );
}
